import React, { useEffect, useMemo, useState } from "react"
import { useMediaQuery } from "@mui/material"
import DesktopProjectsNav from "./projects-nav.desktop"
import MobileProjectsNav from "./projects-nav.mobile"

import { HomepageNavigationItems } from "../../config/nav"
import { Actions, useDispatch, useSelector, useRegisterTrigger, Triggers, useTrigger } from "../../stores/dashboard.store"

import Notifications from "../forms/notifications"
import ProjectList from "../forms/projects-list"
import AccountMenu from "../forms/account-menu"
import CreateProject from "../dialogs/create-project"
import ErrorDialog from "../dialogs/error"

import { fetchCompileStatus, createBlueprint, completeNotifications, pollNotifications, pollQueuedTasks } from "../../api-client/data"

import useMenu from "../../hooks/useMenu"
import useFetch from "../../hooks/useFetch"
import useRunBlueprintStatus from "../../hooks/useRunBlueprintStatus"
import useRefMenu from "../../hooks/useRefMenu"
import useLatest from "../../hooks/useLatest"

import { countDistinctTaskNotifications } from "./projects-nav.util"
import usePolling from "../../hooks/usePolling"

export default function NavigationContainer({ setActiveProject, setActivePage, navItems }) {
    const dispatch = useDispatch()
    const {
        activeProject,
        dashboardView,
        activeProjectRequiresSave,
        user,
        errorMessage,
        loadingProjectId,
        unfulfilledNodes,
        loadingIndeterminate,
        projects,
    } = useSelector()

    const isMobile = useMediaQuery("(max-width: 900px)")

    const [showCreateProject, setShowCreateProject] = useState(false)
    const [hideNotifications, setHideNotifications] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)

    const refreshProjectList = useTrigger(Triggers.RefreshProjectList)
    const submitCreateProject = async (values) => {
        setShowCreateProject(false)
        await createBlueprint(values)
        refreshProjectList()
    }

    const projectMenu = useMenu()
    const accountMenuRef = useRefMenu()
    const notificationsRefMenu = useRefMenu()

    const queuedTasks = usePolling(pollQueuedTasks, 10000, [], true)
    const notifications = usePolling(pollNotifications, 5000, [], true)

    const compileStatus = useFetch(() => fetchCompileStatus(activeProject?.id))
    useRunBlueprintStatus(compileStatus?.data?.status)
    useRegisterTrigger(Triggers.CompileStatus, compileStatus.refresh)

    const loadingProjectIdRef = useLatest(loadingProjectId)

    useEffect(() => {
        if (loadingProjectIdRef.current !== undefined) {
            dispatch(Actions.SetLoadingProjectId, undefined)
        }
        dispatch(Actions.SetTasks, queuedTasks)
    }, [queuedTasks])

    useEffect(() => {
        dispatch(Actions.SetCompileStatus, compileStatus.data)
    }, [compileStatus.data])

    const openNotificationsMenu = () => {
        setHideNotifications(true)
        notificationsRefMenu?.setShow(true)
        completeNotifications()
    }

    const navigateDashboard = () => {
        dispatch(Actions.SetActiveProject, undefined)
        dispatch(Actions.NavigateDashboard, {
            nav: HomepageNavigationItems._id,
            view: HomepageNavigationItems.ProjectList,
        })
    }

    const activeNotificationsCount = useMemo(
        () => countDistinctTaskNotifications(notifications),
        [notifications]
    )

    useEffect(() => {
        setHideNotifications(false)
    }, [activeNotificationsCount])

    const hasUnfulfilledNodes = useMemo(
        () => Object.values(unfulfilledNodes).reduce((a, c) => a + c?.length, 0) > 0,
        [unfulfilledNodes]
    )
    const isTaskQueued = useMemo(
        () => !!queuedTasks?.find((x) => x?.blueprintId === activeProject?.id),
        [queuedTasks, activeProject?.id]
    )
    const isTaskLoading = useMemo(
        () => loadingProjectId === activeProject?.id,
        [loadingProjectId, activeProject?.id]
    )

    const Component = useMemo(() => {
        if (!navItems || !dashboardView?.id) return null
        return navItems[dashboardView.id]?.Component || null
    }, [navItems, dashboardView])

    const commonProps = {
        user,
        errorMessage,
        setErrorMessage: () => dispatch(Actions.SetDashboardError, undefined),
        showCreateProject,
        setShowCreateProject,
        submitCreateProject,
        projectMenu,
        openNotificationsMenu,
        notificationsRefMenu,
        hideNotifications,
        activeNotificationsCount,
        accountMenuRef,
        notifications,
        navigateDashboard,
        setActiveProject,
        projects,
        setActivePage,
        navItems,
        dashboardView,
        hasUnfulfilledNodes,
        activeProjectRequiresSave,
        isTaskQueued,
        isTaskLoading,
        loadingIndeterminate,
        drawerOpen,
        setDrawerOpen,
    }

    if (user===undefined) return null

    return (
        <>
            <ErrorDialog
                details={errorMessage}
                show={!!errorMessage}
                setShow={commonProps.setErrorMessage}
            />
            <CreateProject
                show={showCreateProject}
                onClose={() => setShowCreateProject(false)}
                onSubmit={submitCreateProject}
            />
            <ProjectList
                buttonRef={projectMenu.element}
                open={projectMenu.open}
                onClose={projectMenu.handleClose}
                projects={projects}
                onCreateProject={() => {setShowCreateProject(true); projectMenu.handleClose()}}
                onNavigateProject={setActiveProject}
                style={{ marginTop: 40, marginLeft: "40%", zIndex: 999999 }}
            />
            <AccountMenu
                buttonRef={accountMenuRef?.ref}
                open={accountMenuRef?.show}
                setOpen={accountMenuRef.setShow}
                user={user}
            />
            <Notifications
                buttonRef={notificationsRefMenu?.ref}
                open={notificationsRefMenu?.show}
                setOpen={notificationsRefMenu.setShow}
                notifications={notifications}
                resetNotifications={notifications.reset}
            />

            {isMobile ? (
                <MobileProjectsNav Component={Component} {...commonProps} />
            ) : (
                <DesktopProjectsNav Component={Component} {...commonProps} />
            )}
        </>
    )
}
